// Cloudflare Turnstile widget — invisible-friendly anti-bot challenge.
//
// Usage:
//   <Turnstile onVerify={(token) => setToken(token)} />
//
// The form must submit the token to the server (we use a hidden input
// named `cf-turnstile-response` by convention, matching CF docs). Server
// then POSTs to /api/turnstile/verify which calls siteverify.

import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn.js';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

function loadScriptOnce() {
  if (typeof window === 'undefined') return;
  if (document.querySelector(`script[src^="${SCRIPT_SRC}"]`)) return;
  const s = document.createElement('script');
  s.src = SCRIPT_SRC + '?render=explicit';
  s.async = true;
  s.defer = true;
  document.head.appendChild(s);
}

export default function Turnstile({
  siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY,
  onVerify,
  onExpire,
  onError,
  theme = 'auto',
  className = '',
}) {
  const ref = useRef(null);
  const widgetIdRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    loadScriptOnce();
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      if (window.turnstile && ref.current) {
        widgetIdRef.current = window.turnstile.render(ref.current, {
          sitekey: siteKey,
          theme,
          callback: (token) => onVerify?.(token),
          'expired-callback': () => onExpire?.(),
          'error-callback': () => onError?.(),
        });
        setReady(true);
      } else {
        setTimeout(tick, 50);
      }
    };
    tick();
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // widget already gone
        }
      }
    };
  }, [siteKey, theme, onVerify, onExpire, onError]);

  if (!siteKey) {
    return (
      <div
        className={cn(
          'rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800',
          className,
        )}
        role="alert"
      >
        Turnstile site key missing — set VITE_TURNSTILE_SITE_KEY.
      </div>
    );
  }

  return <div ref={ref} className={className} aria-busy={!ready} />;
}
